// Port of ios/Sources/ViewModels/BlockMarkdown.swift.
//
// Block-level Markdown structure (PARITY: "Block Markdown"), pure and line-based. The INLINE
// grammar (bold, links, mentions, code spans) stays `InlineMarkdown`'s job — this object only
// decides what KIND of block each run of lines is, so the renderer can give each its shape.
//
// Deliberately the pragmatic subset the desktop renders and this team actually types: fenced
// code, headings, quotes, bullet and numbered lists, horizontal rules, pipe tables,
// paragraphs. Anything malformed degrades to a paragraph: the reader always gets their text.
//
// WEB: Swift's `enum Block` with associated values is a discriminated union on `kind`; the
// `Block` factory object below spells the Swift cases (`Block.paragraph("…")`). Lines split on
// "\n" exactly as `components(separatedBy: "\n")` does (a "\r\n" leaves its "\r" on the line,
// where `trimmingCharacters(in: .whitespaces)` does not remove it either).

import { characters, isNumber, trimWhitespaces } from './swiftCharacters';

export type Block =
  | { kind: 'paragraph'; text: string }
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'quote'; text: string }
  | { kind: 'bullets'; items: string[] }
  /** `start`: the number the author gave the FIRST item — a list resuming at "8." reads 8, 9, 10… */
  | { kind: 'numbered'; start: number; items: string[] }
  | { kind: 'codeFence'; code: string }
  | { kind: 'rule' }
  /**
   * A GFM pipe table. Alignments are not modeled: the webapp left-aligns everything in
   * chat. A header whose cells are all blank (the automation bots emit `| | |`) is kept
   * — the renderer decides to hide it, because "no header" is presentation.
   */
  | { kind: 'table'; header: string[]; rows: string[][] };

/** The Swift cases as constructors. */
export const Block = {
  paragraph: (text: string): Block => ({ kind: 'paragraph', text }),
  heading: (level: number, text: string): Block => ({ kind: 'heading', level, text }),
  quote: (text: string): Block => ({ kind: 'quote', text }),
  bullets: (items: string[]): Block => ({ kind: 'bullets', items }),
  numbered: (items: string[], start = 1): Block => ({ kind: 'numbered', start, items }),
  codeFence: (code: string): Block => ({ kind: 'codeFence', code }),
  rule: { kind: 'rule' } as Block,
  table: (header: string[], rows: string[][]): Block => ({ kind: 'table', header, rows }),
} as const;

function parse(message: string): Block[] {
  const blocks: Block[] = [];
  const lines = message.split('\n');
  let i = 0;
  let paragraph = '';

  const flushParagraph = () => {
    if (paragraph !== '') {
      blocks.push(Block.paragraph(paragraph));
      paragraph = '';
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = trimWhitespaces(line);
    if (trimmed.startsWith('```')) {
      flushParagraph();
      let code = '';
      i += 1;
      while (i < lines.length && !trimWhitespaces(lines[i]).startsWith('```')) {
        if (code !== '') code += '\n';
        code += lines[i];
        i += 1;
      }
      // An unclosed fence still renders as code: the author's intent was plain.
      blocks.push(Block.codeFence(code));
      i += 1;
    } else if (isRule(trimmed)) {
      flushParagraph();
      blocks.push(Block.rule);
      i += 1;
    } else if (headingOf(trimmed) !== null) {
      const heading = headingOf(trimmed)!;
      flushParagraph();
      blocks.push(Block.heading(heading[0], heading[1]));
      i += 1;
    } else if (trimmed.startsWith('>')) {
      flushParagraph();
      let quote = '';
      while (i < lines.length) {
        const quoteLine = trimWhitespaces(lines[i]);
        if (!quoteLine.startsWith('>')) break;
        if (quote !== '') quote += '\n';
        quote += trimWhitespaces(quoteLine.slice(1));
        i += 1;
      }
      blocks.push(Block.quote(quote));
    } else if (bulletItem(trimmed) !== null) {
      flushParagraph();
      const items: string[] = [];
      let item: string | null;
      while (i < lines.length && (item = bulletItem(trimWhitespaces(lines[i]))) !== null) {
        items.push(item);
        i += 1;
      }
      blocks.push(Block.bullets(items));
    } else if (numberedItem(trimmed) !== null) {
      flushParagraph();
      const start = numberedItem(trimmed)!.number;
      const items: string[] = [];
      let item: { number: number; text: string } | null;
      while (i < lines.length && (item = numberedItem(trimWhitespaces(lines[i]))) !== null) {
        items.push(item.text);
        i += 1;
      }
      blocks.push(Block.numbered(items, start));
    } else if (looksLikeTableHeader(trimmed, i + 1 < lines.length ? trimWhitespaces(lines[i + 1]) : null)) {
      flushParagraph();
      const header = splitTableRow(trimmed);
      i += 2; // the header and its delimiter row
      const rows: string[][] = [];
      while (i < lines.length) {
        const rowLine = trimWhitespaces(lines[i]);
        if (rowLine === '' || !rowLine.includes('|')) break;
        rows.push(splitTableRow(rowLine));
        i += 1;
      }
      blocks.push(Block.table(header, rows));
    } else if (trimmed === '') {
      flushParagraph();
      i += 1;
    } else {
      if (paragraph !== '') paragraph += '\n';
      paragraph += line;
      i += 1;
    }
  }
  flushParagraph();
  return blocks;
}

function headingOf(line: string): [number, string] | null {
  if (!line.startsWith('#')) return null;
  const chars = characters(line);
  let level = 0;
  while (chars[level] === '#' && level < 7) level += 1;
  if (!(level >= 1 && level <= 6) || chars[level] !== ' ') return null;
  return [level, trimWhitespaces(chars.slice(level + 1).join(''))];
}

function bulletItem(line: string): string | null {
  if (!(line.startsWith('- ') || line.startsWith('* '))) return null;
  return line.slice(2);
}

function numberedItem(line: string): { number: number; text: string } | null {
  const chars = characters(line);
  let digits = 0;
  while (digits < chars.length && isNumber(chars[digits]) && digits < 4) digits += 1;
  if (!(digits >= 1 && digits <= 3)) return null;
  if (chars[digits] !== '.' && chars[digits] !== ')') return null;
  if (chars[digits + 1] !== ' ') return null;
  const number = Number(chars.slice(0, digits).join(''));
  if (!Number.isInteger(number)) return null;
  return { number, text: chars.slice(digits + 2).join('') };
}

/**
 * A run of one marker character (`-` `*` `_`), at least three, spaces tolerated. A dash
 * INSIDE a sentence is text; "2 - 3" must never become a rule.
 */
function isRule(line: string): boolean {
  if (line === '') return false;
  let marker: string | null = null;
  let count = 0;
  for (const character of characters(line)) {
    if (character === ' ' || character === '\t') continue;
    if (marker === null) {
      if (!(character === '-' || character === '*' || character === '_')) return false;
      marker = character;
      count = 1;
    } else if (character === marker) {
      count += 1;
    } else {
      return false;
    }
  }
  return count >= 3;
}

/**
 * The row under a table header: pipes and dash runs (`| - | --- |`, `:---:` tolerated),
 * nothing else. Requiring a pipe is what keeps a lone `---` a rule.
 */
function isTableDelimiter(line: string): boolean {
  if (!line.includes('|')) return false;
  const cells = splitTableRow(line);
  if (cells.length === 0) return false;
  return cells.every((cell) => {
    let body = characters(cell);
    if (body[0] === ':') body = body.slice(1);
    if (body[body.length - 1] === ':') body = body.slice(0, -1);
    return body.length > 0 && body.every((c) => c === '-');
  });
}

function looksLikeTableHeader(line: string, next: string | null): boolean {
  if (!line.includes('|') || next === null || !next.includes('|')) return false;
  return isTableDelimiter(next);
}

/**
 * `| a | b |` → ["a", "b"]: one optional outer pipe stripped from each end, cells trimmed.
 * Escaped pipes are not handled — a wrong split degrades to an extra column, never to
 * lost text.
 */
function splitTableRow(line: string): string[] {
  let s = trimWhitespaces(line);
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map(trimWhitespaces);
}

export const BlockMarkdown = {
  parse,
} as const;
